import { Currencies } from "@finway-group/shared/lib/consts"
import { CurrencyEnum } from "@finway-group/shared/lib/models"
import { type Address } from "@finway-group/shared/lib/models/company/address.interface"
import {
    PURCHASE_ORDER_DESCRIPTION_MAX_LENGTH,
    PURCHASE_ORDER_DESCRIPTION_MIN_LENGTH,
    PURCHASE_ORDER_NUMBER_MAX_LENGTH,
    PURCHASE_ORDER_NUMBER_MIN_LENGTH,
    PURCHASE_ORDER_PAYMENT_TERMS_MAX_LENGTH,
    PURCHASE_ORDER_PAYMENT_TERMS_MIN_LENGTH,
    PURCHASE_ORDER_PRICE_MIN_BIGGER_THAN,
} from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrder.validation"
import { isDateGobdCompliant, isPerformanceDateWithinHardLimit } from "@finway-group/shared/lib/utils"
import { validateAddressFormat } from "@finway-group/shared/lib/utils/validators/address.validator"
import { isAmountBelowCurrencyReasonableLimit } from "@finway-group/shared/lib/utils/validators/currencyLimit.validator"
import { type Rule } from "antd/lib/form"
import isEmpty from "lodash/isEmpty"
import { type Moment } from "moment"
import { dateRules } from "~Components/forms/rules/common/date.rules"
import { generalRules } from "~Components/forms/rules/common/general.rules"
import { stringRules } from "~Components/forms/rules/common/string.rules"
import { i18n } from "~Shared/locales/i18n"
import { formatAddress } from "~Shared/utils/form.utils"
import { isSet } from "~Shared/utils/helper.utils"

export const createPurchaseOrderFormRules = () => ({
    dateOfIssue: [dateRules.gobdCompliant] as Array<Rule>,
    deliveryLocation: [
        {
            validator: (_rule, value: Address | string) => {
                if (!value || (typeof value === "object" && isEmpty(value.addressLine1) && isEmpty(value.zip) && isEmpty(value.city) && isEmpty(value.country))) {
                    return Promise.resolve()
                }
                const formattedAddress = typeof value === "string" ? value : formatAddress(value)

                // If there's a value, validate the format
                return validateAddressFormat(formattedAddress) ? Promise.resolve() : Promise.reject(new Error(i18n.t("validation:invalid_address_format")))
            },
        },
    ] as Array<Rule>,
    description: [
        generalRules.required,
        stringRules.noExcessWhitespace,
        stringRules.minLength(PURCHASE_ORDER_DESCRIPTION_MIN_LENGTH),
        stringRules.maxLength(PURCHASE_ORDER_DESCRIPTION_MAX_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    expectedFullfilmentDate: [
        ({ getFieldValue }) => ({
            required: getFieldValue("isService"),
            validator: (_rule, value: Moment) => {
                const isServiceValue = getFieldValue("isService")

                if (!value) {
                    if (isServiceValue) {
                        return Promise.reject(new Error(i18n.t("validation:required")))
                    }
                    return Promise.resolve()
                }

                const dateOfIssueFormValue = getFieldValue("dateOfIssueForm")

                if (isSet(dateOfIssueFormValue) && value.isBefore(dateOfIssueFormValue)) {
                    return Promise.reject(
                        i18n.t("validation:date_after", {
                            attribute: isServiceValue ? i18n.t("purchaseOrders:drawer.expectedServiceDate") : i18n.t("purchaseOrders:drawer.expectedDeliveryDate"),
                            compare: i18n.t("purchaseOrders:drawer.dateOfIssue"),
                        }),
                    )
                }

                if (!isDateGobdCompliant(value)) {
                    return Promise.reject(new Error(i18n.t("validation:out_of_range.date")))
                }

                return Promise.resolve()
            },
        }),
    ] as Array<Rule>,
    paymentTerms: [
        stringRules.noExcessWhitespace,
        stringRules.minLength(PURCHASE_ORDER_PAYMENT_TERMS_MIN_LENGTH),
        stringRules.maxLength(PURCHASE_ORDER_PAYMENT_TERMS_MAX_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    performancePeriod: [
        {
            validator: (_rule, value: [Moment, Moment]) => {
                if (!value) {
                    return Promise.resolve()
                }

                if (!isPerformanceDateWithinHardLimit(value[0]) || !isPerformanceDateWithinHardLimit(value[1])) {
                    return Promise.reject(new Error(i18n.t("validation:out_of_range.date")))
                }

                if (value[0].isAfter(value[1])) {
                    return Promise.reject(new Error(i18n.t("validation:date_before", { attribute: i18n.t("validation:start_date"), compare: i18n.t("validation:end_date") })))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    purchaseOrderNumber: [
        stringRules.noExcessWhitespace,
        stringRules.minLength(PURCHASE_ORDER_NUMBER_MIN_LENGTH),
        stringRules.maxLength(PURCHASE_ORDER_NUMBER_MAX_LENGTH),
        stringRules.allowedCompanyNameCharsOnly,
    ] as Array<Rule>,
    tags: [
        {
            validator: (_rule, value: Array<string>) => {
                if (!value) {
                    return Promise.resolve()
                }

                if (value.some((tag: unknown) => !(typeof tag === "string"))) {
                    return Promise.reject(new Error(i18n.t("validation:invalid")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    totalGrossPrice: [
        ({ getFieldValue }) => ({
            validator: (_rule, value: number) => {
                if (!value) {
                    return Promise.reject(new Error(i18n.t("validation:required")))
                }

                if (value <= PURCHASE_ORDER_PRICE_MIN_BIGGER_THAN) {
                    return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: PURCHASE_ORDER_PRICE_MIN_BIGGER_THAN })))
                }

                const currencyValue = getFieldValue("currency") ?? CurrencyEnum.EUR

                if (!isAmountBelowCurrencyReasonableLimit(currencyValue, value)) {
                    return Promise.reject(new Error(i18n.t("validation:below.number_short", { max: Currencies[currencyValue].max_allowed_value })))
                }

                return Promise.resolve()
            },
        }),
    ] as Array<Rule>,
})
